import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import L from 'leaflet';
import { SecurityInventoryCategory, SecurityInventoryItem } from '@x-guard/xgac-types/xgac';
import {
  Identifier, useTranslate, Confirm, useRedirect,
} from 'react-admin';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  useGetList, useRefresh, useNotify, useGetIdentity,
  useGetOne, HttpError,
} from 'ra-core';
import { useSearchParams } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton,
} from '@mui/material';
import { useDebounce } from 'use-debounce';
import {
  hasItemPosition, itemToLatLng, securityItemsResource, appUsersResource,
  InventoryItemFilter,
  InventoryItemStatus,
} from '../util';
import { BulkActionsSection } from './BulkActionsSection';
import { CreateItemSection } from './CreateItemSection';
import { FilterButton } from './FilterButton';
import { SearchInput } from '../../../components/SearchInput';
import { SelectAllButton } from './SelectAllButton';
import { InventoryItemsDatagrid } from './InventoryItemsDatagrid';
import { xgacDataProvider } from '../../../../../dataProviders/xgacDataProvider';
import { EditItemModal } from './EditItemModal';
import { RenewalChoiceDialog } from '../../../components/RenewalChoiceDialog';
import { RenewNowFormValues, RenewNowModal } from '../../../components/RenewNowModal';
import { PlanRenewalModal } from '../../../components/PlanRenewalModal';
import { notifyWithError } from '../../../../../utils/errorHandling';
import { getExpiryStatus } from '../../../../../utils/certificationsAndItemsExpiry';
import { calculateLargestReminderPeriodInDays } from '../../../../../utils/timeGranularityUtils';
import { RaXgacRecord } from '../../../../../lib/RaXgacRecord';
import { getCurrentCustomer } from '../../../../../lib/currentCustomer';

export const InventoryItemsSidebar = (props: {
  itemsData: SecurityInventoryItem[];
  itemsLoading: boolean;
  categoriesData: SecurityInventoryCategory[];
  categoriesLoading: boolean;
  map: L.Map | null;
  focusedItemId: Identifier | null;
  onItemFocus: (itemId: Identifier | null) => void;
  hoveredItemId: Identifier | null;
  onItemHover: (itemId: Identifier | null) => void;
  onItemsFiltered: (items: SecurityInventoryItem[]) => void;
  onVisibleItemsChange: (visibleItemIds: Set<Identifier>) => void;
}) => {

  const redirect = useRedirect();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  const [selectedIds, setSelectedIds] = useState<Identifier[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [editItemModalOpen, setEditItemModalOpen] = useState(false);
  const [editItemRecord, setEditItemRecord] = useState<SecurityInventoryItem | null>(null);
  const idsBeforeCreate = useRef<Set<Identifier>>(new Set());
  const [openEditAfterCreate, setOpenEditAfterCreate] = useState(false);
  const [createItemFlowActive, setCreateItemFlowActive] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewDialogChoice, setRenewDialogChoice] = useState<'renew_now' | 'plan_renewal' | 'closed'>('closed');
  const [renewFlowSessionKey, setRenewFlowSessionKey] = useState(0);
  const [currentUserItemsExpanded, setCurrentUserItemsExpanded] = useState(false);
  const [otherItemsExpanded, setOtherItemsExpanded] = useState(false);
  const [currentUserItemsVisible, setCurrentUserItemsVisible] = useState(true);
  const [otherItemsVisible, setOtherItemsVisible] = useState(true);

  const accordionInitialized = useRef(false);

  const showBulkActions = selectedIds.length > 0;
  const map = props.map;
  const onItemFocus = props.onItemFocus;
  const onItemsFiltered = props.onItemsFiltered;
  const onVisibleItemsChange = props.onVisibleItemsChange;

  const editItemId = searchParams.get('editItem');
  const categoryId = searchParams.get('category');
  const statusParam = searchParams.get('status');

  const status: InventoryItemStatus | null = statusParam === 'valid' || statusParam === 'expiring' || statusParam === 'expired'
    ? statusParam : null;

  const [selectedFilters, setSelectedFilters] = useState<InventoryItemFilter>({ categories: categoryId ? [categoryId] : [], status: status ? [status] : [] });

  const createItemCategoryId = searchParams.get('createItem');

  const { data: identity, isLoading: identityLoading } = useGetIdentity();

  // For all of the fetched resources, all of the data is fetched at once without pagination
  const { data: usersData, isLoading: usersLoading } = useGetList(appUsersResource, {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  // A single item is fetched to check to which customer it belongs to.
  // This is done because we cannot use an item from itemsData since useGetList filters by customer.
  const {
    data: linkedItemData,
    error: linkedItemError,
    isPending: isLinkedItemPending,
  } = useGetOne<RaXgacRecord<SecurityInventoryItem>>(
    securityItemsResource,
    { id: editItemId ?? undefined },
    {
      enabled: Boolean(editItemId),
      retry: (retryCount, error) => {

        if (error instanceof HttpError && error.status === 404) return false;
        return retryCount < 3;

      },
    },
  );

  const currentAssetId = useMemo(
    () => {

      if (identityLoading || usersLoading || !identity?.id) return null;

      return usersData?.find((user) => user.user?._id && user.user._id === identity.id)?._id;

    },
    [usersData, identity?.id, identityLoading, usersLoading],
  );

  const peopleToNotifyByCategoryId = useMemo(() => {

    const peopleToNotify = new Map<string, Set<string>>();
    for (const category of props.categoriesData) {

      peopleToNotify.set(
        category._id,
        new Set(
          (category.notifiedPeople?.peopleToNotify ?? [])
            .map((entry) => (typeof entry === 'string' ? entry : entry._id)),
        ),
      );

    }
    return peopleToNotify;

  }, [props.categoriesData]);

  // Both search and filter are applied together
  const filteredItems = useMemo(() => {

    if (props.itemsLoading || props.categoriesLoading) return [];

    const list = props.itemsData ?? [];
    const q = debouncedSearchTerm.trim().toLowerCase();

    const { categories: selectedCategories, status: selectedStatuses } = selectedFilters;

    const reminderDaysByCategoryId = Object.fromEntries(
      props.categoriesData.map((category) => [
        category._id,
        calculateLargestReminderPeriodInDays(category.reminderPeriods ?? []),
      ]),
    );

    return list.filter((item) => {

      if (q && !(item.name.toLowerCase().includes(q))) return false;

      const expiryStatus = getExpiryStatus(item.expiryDate, reminderDaysByCategoryId[item.category._id]);
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(expiryStatus)) return false;

      if (selectedCategories.length > 0 && !selectedCategories.includes(item.category._id)) return false;

      return true;

    });

  }, [props.itemsLoading, props.categoriesLoading, props.itemsData, props.categoriesData, debouncedSearchTerm, selectedFilters]);

  const [currentUserItems, remainingItems] = useMemo<[SecurityInventoryItem[], SecurityInventoryItem[]]>(() => {

    const userItems: SecurityInventoryItem[] = [];
    const otherItems: SecurityInventoryItem[] = [];

    for (const item of filteredItems) {

      if (currentAssetId && peopleToNotifyByCategoryId.get(item.category._id)?.has(currentAssetId)) {

        userItems.push(item);

      } else {

        otherItems.push(item);

      }

    }
    return [userItems, otherItems];

  }, [currentAssetId, filteredItems, peopleToNotifyByCategoryId]);

  useEffect(() => {

    if (props.itemsLoading || props.categoriesLoading || identityLoading || usersLoading || accordionInitialized.current) return;

    accordionInitialized.current = true;
    setCurrentUserItemsExpanded(currentUserItems.length > 0);
    setOtherItemsExpanded(remainingItems.length > 0);

  }, [props.itemsLoading, props.categoriesLoading, identityLoading, usersLoading, currentUserItems.length, remainingItems.length]);

  // Items that are available for selection (expanded and NOT hidden with the eye icon)
  const { selectableItems, selectableItemIds } = useMemo(() => {

    let itemsAvailableForSelection: SecurityInventoryItem[] = [];

    const currentUserItemsSelectable = currentUserItemsExpanded && currentUserItemsVisible;
    const otherItemsSelectable = otherItemsExpanded && otherItemsVisible;

    // if both item groups are expanded and visible, make all filtered items available for selection
    if (currentUserItemsSelectable && otherItemsSelectable) itemsAvailableForSelection = filteredItems;
    // if only the current user items are expanded and visible, make only those items available for selection
    if (currentUserItemsSelectable && !otherItemsSelectable) itemsAvailableForSelection = currentUserItems;
    // if only the other items are expanded and visible, make only those items available for selection
    if (!currentUserItemsSelectable && otherItemsSelectable) itemsAvailableForSelection = remainingItems;

    return {
      selectableItems: itemsAvailableForSelection,
      selectableItemIds: new Set<Identifier>(itemsAvailableForSelection.map((item) => item._id)),
    };

  }, [
    currentUserItemsExpanded, otherItemsExpanded, currentUserItemsVisible, otherItemsVisible,
    filteredItems, currentUserItems, remainingItems,
  ]);

  // Items that are visible on the map (expanded/collapsed and NOT hidden with the eye icon)
  const visibleItemIds = useMemo(() => {

    let mapItems: SecurityInventoryItem[] = [];

    // if both item groups are visible, show all filtered items on the map
    if (currentUserItemsVisible && otherItemsVisible) mapItems = filteredItems;
    // if only the current user items are visible, show only those items on the map
    if (currentUserItemsVisible && !otherItemsVisible) mapItems = currentUserItems;
    // if only the other items are visible, show only those items on the map
    if (!currentUserItemsVisible && otherItemsVisible) mapItems = remainingItems;

    return new Set<Identifier>(mapItems.map((item) => item._id));

  }, [currentUserItemsVisible, otherItemsVisible, filteredItems, currentUserItems, remainingItems]);

  useEffect(() => {

    onVisibleItemsChange(visibleItemIds);

  }, [visibleItemIds, onVisibleItemsChange]);

  const selectedItems = useMemo(
    () => props.itemsData.filter((item) => selectedIds.includes(item._id)),
    [props.itemsData, selectedIds],
  );

  // Users can select items from multiple categories, but we need to use something to pre-calculate expiry date with.
  // For this purpose, we will use the category of the majority (but users still can edit the expiry date manually)
  const primaryCategory = useMemo(() => {

    if (selectedItems.length === 0) return null;
    const counts = new Map<string, number>();
    for (const item of selectedItems) {

      const currentCategoryId = item.category?._id;
      if (currentCategoryId) {

        counts.set(currentCategoryId, (counts.get(currentCategoryId) ?? 0) + 1);

      }

    }
    if (counts.size === 0) return null;
    let topCategoryId: string | null = null;
    let topCount = 0;
    for (const [currentCategoryId, count] of counts) {

      if (count > topCount) {

        topCount = count;
        topCategoryId = currentCategoryId;

      }

    }
    return props.categoriesData.find((c) => c._id === topCategoryId) ?? null;

  }, [selectedItems, props.categoriesData]);

  const focusItemFromList = useCallback((item: SecurityInventoryItem) => {

    onItemFocus(item._id);
    if (!hasItemPosition(item) || !map) return;

    const latLng = L.latLng(itemToLatLng(item));
    if (!map.getBounds().contains(latLng)) map.flyTo(latLng);

  }, [map, onItemFocus]);

  const closeRenewDialog = () => {

    setRenewDialogChoice('closed');
    setRenewDialogOpen(false);
    setRenewFlowSessionKey((k) => k + 1);

  };

  const updateSelectedItems = async (data: Partial<SecurityInventoryItem>) => {

    if (selectedItems.length === 0) return;
    await Promise.all(
      selectedItems.map((item) => xgacDataProvider.update(securityItemsResource, {
        id: item._id,
        data,
        previousData: item,
      })),
    );

  };

  const openEditItemModal = useCallback((item: SecurityInventoryItem) => {

    setEditItemRecord(item);
    setEditItemModalOpen(true);
    focusItemFromList(item);

  }, [focusItemFromList]);

  const closeEditItemModal = () => {

    setEditItemModalOpen(false);
    setEditItemRecord(null);
    setCreateItemFlowActive(false);

  };

  const clearCreateItemParam = useCallback(() => {

    setSearchParams((params) => {

      const updated = new URLSearchParams(params);
      updated.delete('createItem');
      return updated;

    }, { replace: true });

  }, [setSearchParams]);

  const handleBeforeCreate = useCallback(() => {

    idsBeforeCreate.current = new Set(props.itemsData.map((item) => item._id));
    setCreateItemFlowActive(true);

  }, [props.itemsData]);

  const handleAfterCreate = useCallback(() => {

    setOpenEditAfterCreate(true);

  }, []);

  const handleDeleteItems = async () => {

    if (selectedIds.length === 0) return;
    try {

      await xgacDataProvider.deleteMany(securityItemsResource, { ids: selectedIds });
      notify('ra.notification.deleted', { type: 'info', messageArgs: { smart_count: selectedIds.length } });
      refresh();
      if (props.focusedItemId && selectedIds.includes(props.focusedItemId)) {

        onItemFocus(null);

      }
      setSelectedIds([]);
      setDeleteDialogOpen(false);

    } catch (error) {

      notifyWithError(notify, error);
      setDeleteDialogOpen(false);

    }

  };

  useEffect(() => {

    if (!openEditAfterCreate) return;

    const newItems = props.itemsData.filter((item) => !idsBeforeCreate.current.has(item._id));
    if (newItems.length === 0) return;

    openEditItemModal(newItems[0]);
    setOpenEditAfterCreate(false);

  }, [openEditAfterCreate, props.itemsData, openEditItemModal, setOpenEditAfterCreate]);

  useEffect(() => {

    if (!editItemId || isLinkedItemPending) return;

    const removeEditItemParam = () => {

      setSearchParams((params) => {

        const updatedSearchParams = new URLSearchParams(params);
        updatedSearchParams.delete('editItem');
        return updatedSearchParams;

      }, { replace: true });

    };

    if (linkedItemError || !linkedItemData) {

      notify('resources.security-inventory-items.notifications.item_could_not_be_opened', { type: 'error' });
      removeEditItemParam();
      return;

    }

    const allCustomers: { value: string; label: string }[] = JSON.parse(localStorage.getItem('customers') || '[]');
    const targetCustomer = allCustomers.find((customer) => customer.value === linkedItemData.customer._id);

    if (!targetCustomer) {

      notify('resources.security-inventory-items.notifications.item_could_not_be_opened', { type: 'error' });
      removeEditItemParam();
      return;

    }

    if (targetCustomer.value !== getCurrentCustomer()?.value) {

      sessionStorage.setItem('currentCustomer', JSON.stringify(targetCustomer));
      window.location.reload();
      return;

    }

    openEditItemModal(linkedItemData);
    removeEditItemParam();

  }, [editItemId, linkedItemData, linkedItemError, isLinkedItemPending, notify, openEditItemModal, setSearchParams]);

  useEffect(() => {

    if (!categoryId && !status) return;

    setSelectedFilters((prev) => ({
      ...prev,
      categories: categoryId ? [categoryId] : [],
      status: status ? [status] : [],
    }));

    setSearchParams((current) => {

      const updated = new URLSearchParams(current);
      updated.delete('category');
      updated.delete('status');
      return updated;

    }, { replace: true });

  }, [categoryId, status, setSelectedFilters, setSearchParams]);

  useEffect(() => {

    onItemsFiltered(filteredItems);

  }, [onItemsFiltered, filteredItems]);

  useEffect(() => {

    setSelectedIds((currentIds) => {

      const displayedSelectedIds = currentIds.filter((id) => selectableItemIds.has(id));
      return displayedSelectedIds.length === currentIds.length ? currentIds : displayedSelectedIds;

    });

  }, [selectableItemIds]);

  useEffect(() => {

    if (props.itemsLoading || props.categoriesLoading) return;

    if (props.focusedItemId !== null && !visibleItemIds.has(props.focusedItemId)) {

      onItemFocus(null);

    }

  }, [props.focusedItemId, onItemFocus, visibleItemIds, props.itemsLoading, props.categoriesLoading]);

  useEffect(() => {

    if (!props.focusedItemId) return;
    requestAnimationFrame(() => {

      document.getElementById(`inventory-item-${props.focusedItemId}`)?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });

    });

  }, [props.focusedItemId]);

  const handleRenewNowSubmit = async (values: RenewNowFormValues) => {

    if (!('lastInspectionDate' in values)) return;

    const data = {
      lastInspectionDate: new Date(values.lastInspectionDate).toISOString(),
      expiryDate: new Date(values.expiryDate).toISOString(),
    };
    try {

      await updateSelectedItems(data);
      notify('ra.notification.updated', {
        type: 'success',
        messageArgs: { smart_count: selectedItems.length },
      });
      refresh();
      setSelectedIds([]);
      closeRenewDialog();

    } catch (error) {

      notifyWithError(notify, error);
      closeRenewDialog();

    }

  };

  const handlePlanRenewalSubmit = async (values: {
    plannedRenewalDate: string;
  }) => {

    const data = {
      plannedRenewalDate: new Date(values.plannedRenewalDate).toISOString(),
    };
    try {

      await updateSelectedItems(data);
      notify('ra.notification.updated', {
        type: 'success',
        messageArgs: { smart_count: selectedItems.length },
      });
      refresh();
      setSelectedIds([]);
      closeRenewDialog();

    } catch (error) {

      notifyWithError(notify, error);
      closeRenewDialog();

    }

  };

  return (
    <>
      <div className="button-sidebar-div">
        <div className="button-sidebar-add">
          <Box sx={{
            alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'space-between', marginBottom: '1em', marginTop: '0.5em',
          }}>
            <h4 style={{ margin: 0 }}>
              <span>{showBulkActions ? translate('resources.buttons.text.actions_with_selected') : translate('menu.items.security_inventory')}</span>
            </h4>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => redirect('list', 'security-inventory-categories')}
              aria-hidden={showBulkActions}
              tabIndex={showBulkActions ? -1 : 0}
              sx={{
                marginTop: '2px',
                visibility: showBulkActions ? 'hidden' : 'visible',
                pointerEvents: showBulkActions ? 'none' : 'auto',
              }}
            >
              {translate('general.text.button_settings')}
            </Button>
          </Box>
          <Box sx={{ position: 'relative' }}>
            <Box
              aria-hidden={showBulkActions}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                visibility: showBulkActions ? 'hidden' : 'visible',
                pointerEvents: showBulkActions ? 'none' : 'auto',
              }}
            >
              <CreateItemSection
                categories={props.categoriesData || []}
                categoriesLoading={props.categoriesLoading}
                createItemFlowActive={createItemFlowActive}
                map={map}
                onBeforeCreate={handleBeforeCreate}
                onAfterCreate={handleAfterCreate}
                initialCreateCategoryId={createItemCategoryId}
                onInitialCreateCategory={clearCreateItemParam}
              />
              <FilterButton
                categories={props.categoriesData || []}
                filters={selectedFilters}
                onChange={setSelectedFilters}
              >
                <SearchInput
                  searchTerm={searchTerm}
                  onChange={setSearchTerm}
                  placeholder={translate('resources.security-inventory-items.text.search')}
                  variant="sidebar"
                />
              </FilterButton>
            </Box>
            <Box
              aria-hidden={!showBulkActions}
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                justifyContent: 'flex-start',
                visibility: showBulkActions ? 'visible' : 'hidden',
                pointerEvents: showBulkActions ? 'auto' : 'none',
              }}
            >
              <BulkActionsSection
                onDelete={() => setDeleteDialogOpen(true)}
                onRenew={() => {

                  setRenewDialogOpen(true);
                  setRenewFlowSessionKey((k) => k + 1);

                }}
              />
              <SelectAllButton
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                selectableItems={selectableItems}
              />
            </Box>
          </Box>
        </div>
        <div className="button-sidebar-grid">
          <Accordion
            expanded={currentUserItemsExpanded}
            onChange={(_, expanded) => setCurrentUserItemsExpanded(expanded)}
            disableGutters
            elevation={0}
            slotProps={{ transition: { unmountOnExit: true } }}
            sx={{
              '&::before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 50,
                flexDirection: 'row-reverse',
                '&.Mui-expanded': {
                  minHeight: 50,
                },
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  margin: '6px 0',
                },
                '& .MuiAccordionSummary-content.Mui-expanded': {
                  margin: '6px 0',
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  marginRight: 1,
                },
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>
                {translate('resources.security-inventory-items.text.items_i_am_responsible_for', {
                  count: currentUserItems.length,
                })}
              </Box>
              <IconButton
                size="small"
                onClick={(event) => {

                  event.stopPropagation();
                  setCurrentUserItemsVisible((visible) => !visible);

                }}
              >
                {currentUserItemsVisible ? <VisibilityIcon color="action" /> : <VisibilityOffIcon color="disabled" />}
              </IconButton>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <InventoryItemsDatagrid
                items={currentUserItems}
                isLoading={props.itemsLoading || props.categoriesLoading || usersLoading || identityLoading}
                categories={props.categoriesData || []}
                visible={currentUserItemsVisible}
                selectable={currentUserItemsExpanded && currentUserItemsVisible}
                hoveredItemId={props.hoveredItemId}
                selectedIds={selectedIds}
                onHover={props.onItemHover}
                onSelectionChange={setSelectedIds}
                onFocus={focusItemFromList}
                onEdit={openEditItemModal}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={otherItemsExpanded}
            onChange={(_, expanded) => setOtherItemsExpanded(expanded)}
            disableGutters
            elevation={0}
            slotProps={{ transition: { unmountOnExit: true } }}
            sx={{
              '&::before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 50,
                flexDirection: 'row-reverse',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  margin: '6px 0',
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  marginRight: 1,
                },
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>
                {translate('resources.security-inventory-items.text.other_inventory_items', {
                  count: remainingItems.length,
                })}
              </Box>
              <IconButton
                size="small"
                onClick={(event) => {

                  event.stopPropagation();
                  setOtherItemsVisible((visible) => !visible);

                }}
              >
                {otherItemsVisible ? <VisibilityIcon color="action" /> : <VisibilityOffIcon color="disabled" />}
              </IconButton>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <InventoryItemsDatagrid
                items={remainingItems}
                isLoading={props.itemsLoading || props.categoriesLoading || usersLoading || identityLoading}
                categories={props.categoriesData || []}
                visible={otherItemsVisible}
                selectable={otherItemsExpanded && otherItemsVisible}
                hoveredItemId={props.hoveredItemId}
                selectedIds={selectedIds}
                onHover={props.onItemHover}
                onSelectionChange={setSelectedIds}
                onFocus={focusItemFromList}
                onEdit={openEditItemModal}
              />
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
      <Confirm
        isOpen={deleteDialogOpen}
        onConfirm={handleDeleteItems}
        onClose={() => setDeleteDialogOpen(false)}
        title={translate('resources.security-inventory-items.text.delete_dialog_title')}
        content={translate('resources.security-inventory-items.text.delete_content')}
        confirm="ra.action.delete"
        confirmColor="warning"
      />
      <EditItemModal
        record={editItemRecord}
        categories={props.categoriesData || []}
        users={usersData || []}
        open={editItemModalOpen}
        onClose={closeEditItemModal}
      />
      <RenewalChoiceDialog
        variant="security-inventory"
        open={renewDialogOpen}
        onClose={closeRenewDialog}
        onRenewNow={() => {

          setRenewDialogChoice('renew_now');
          setRenewDialogOpen(false);

        }}
        onPlanRenewal={() => {

          setRenewDialogChoice('plan_renewal');
          setRenewDialogOpen(false);

        }}
      />
      <RenewNowModal
        startDateSource="lastInspectionDate"
        formSessionKey={renewFlowSessionKey}
        open={renewDialogChoice === 'renew_now'}
        onClose={closeRenewDialog}
        onBack={() => {

          setRenewDialogChoice('closed');
          setRenewDialogOpen(true);

        }}
        onSubmit={handleRenewNowSubmit}
        timeGranularity={primaryCategory?.expiryInterval}
        title="resources.security-inventory-items.text.renew_now_modal_title"
        startDateLabel="resources.security-inventory-items.fields.last_inspection_date"
        expiryDateLabel="resources.security-inventory-items.fields.expiry_date"
        helperText="resources.security-inventory-items.text.renew_now_modal_helper_text"
      />
      <PlanRenewalModal
        formSessionKey={renewFlowSessionKey}
        open={renewDialogChoice === 'plan_renewal'}
        onClose={closeRenewDialog}
        onBack={() => {

          setRenewDialogChoice('closed');
          setRenewDialogOpen(true);

        }}
        onSubmit={handlePlanRenewalSubmit}
        dateFieldLabel="resources.security-inventory-items.fields.planned_inspection_date"
        dateFieldHelperText="resources.security-inventory-items.text.planned_inspection_date_helper_text"
        title="resources.security-inventory-items.text.plan_inspection"
      />
    </>
  );

};
